import dayjs, { Dayjs } from "dayjs";
import { useDayjsDateStringParamState } from "hooks/useDayjsDateStringParamState";
import { createContext, ReactNode, use, useMemo } from "react";

interface DateRangeContextType {
  startDate: Dayjs;
  setStartDate: (date: Dayjs) => void;
  endDate: Dayjs;
  setEndDate: (date: Dayjs) => void;
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(
  undefined
);

export interface DateRangeConfig {
  paramPrefix: string;
  defaultStartDate: Dayjs;
  defaultEndDate: Dayjs;
}

export const DEFAULT_DATE_RANGE_CONFIG: DateRangeConfig = {
  paramPrefix: "sd",
  defaultStartDate: dayjs().subtract(1, "month").startOf("month"),
  defaultEndDate: dayjs(),
};

export const COMPLIANCE_DATE_RANGE_CONFIG: DateRangeConfig = {
  paramPrefix: "compliance",
  defaultStartDate: dayjs().startOf("month"),
  defaultEndDate: dayjs(),
};

export const DELIVERIES_DATE_RANGE_CONFIG: DateRangeConfig = {
  paramPrefix: "deliveries",
  defaultStartDate: dayjs().startOf("month"),
  defaultEndDate: dayjs(),
};

export const SCHEDULE_DATE_RANGE_CONFIG: DateRangeConfig = {
  paramPrefix: "schedule",
  defaultStartDate: dayjs(),
  defaultEndDate: dayjs().add(1, "month"),
};

interface DateRangeProviderProps {
  children: ReactNode;
  config: DateRangeConfig;
}

export const DateRangeProvider = ({
  children,
  config,
}: DateRangeProviderProps) => {
  const prefix = `${config.paramPrefix}_`;
  const defaultStart = config.defaultStartDate;
  const defaultEnd = config.defaultEndDate;

  const [startDate, setStartDate] = useDayjsDateStringParamState(
    `${prefix}startDate`,
    defaultStart
  );

  const [endDate, setEndDate] = useDayjsDateStringParamState(
    `${prefix}endDate`,
    defaultEnd
  );

  const value = useMemo(
    () => ({
      startDate,
      setStartDate,
      endDate,
      setEndDate,
    }),
    [startDate, setStartDate, endDate, setEndDate]
  );

  return <DateRangeContext value={value}>{children}</DateRangeContext>;
};

export const useDateRange = () => {
  const context = use(DateRangeContext);
  if (context === undefined) {
    throw new Error("useDateRange must be used within a DateRangeProvider");
  }
  return context;
};
